import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { type Dayjs } from "dayjs";
import { parseAsInteger, useQueryStates } from "nuqs";

import { type SalesMeetingSaleStatus, type SalesMeeting } from "@fitness-app/data-models/entities/SalesMeeting";
import { type TeamMember } from "@fitness-app/data-models/entities/TeamMember";

import { supabase } from "~/store/initializeStore";

export const SALES_MEETINGS_PAGE_SIZE = 50;
export const SALE_STATUS_EMPTY_FILTER = "__empty__";

export type SaleStatusFilterValue = SalesMeetingSaleStatus | typeof SALE_STATUS_EMPTY_FILTER;

export type SalesMeetingBundle = {
  id: string;
  name: string;
  clientEmail: string;
  amount: number;
  totalAmount: number;
  currency: string;
  createdAt: string;
  cycles: number | null;
  initialOrderId: string | null;
  productId: string | null;
  type: string;
};

export type SalesMeetingRow = SalesMeeting & {
  teamMember?: Pick<TeamMember, "id" | "firstName" | "lastName" | "email" | "calendlyEmail"> | null;
  customerBundle?: SalesMeetingBundle | null;
  matchedCustomerBundle?: SalesMeetingBundle | null;
};

export type SalesMeetingsDateMode = "meeting" | "sale";

type UseSalesMeetingsQueryParams = {
  range: [Dayjs, Dayjs];
  sellerId: string | null;
  enabled: boolean;
  saleStatusFilters?: SaleStatusFilterValue[];
  paginated?: boolean;
  queryScope?: string;
  dateMode?: SalesMeetingsDateMode;
  emailSearch?: string;
  /** Tylko sprzedaże, w których klient wycofał się po okresie warmup (TRE-1528). */
  postWarmupWithdrawalOnly?: boolean;
};

export const salesMeetingBundleSelectFields =
  "id, name, clientEmail, amount, totalAmount, currency, createdAt, cycles, initialOrderId, productId, type";

const getSaleStatusValues = (saleStatusFilters: SaleStatusFilterValue[]) =>
  saleStatusFilters.filter((value): value is SalesMeetingSaleStatus => value !== SALE_STATUS_EMPTY_FILTER);

// Strip characters that are structural in PostgREST `or(...)` filter strings
// and escape LIKE wildcards so e.g. underscores in e-mails match literally.
const escapePostgrestPattern = (value: string) =>
  value
    .replace(/[,()]/g, " ")
    .replace(/[%_]/g, (wildcard) => `\\${wildcard}`)
    .trim();

export const useSalesMeetingsQuery = ({
  range,
  sellerId,
  enabled,
  saleStatusFilters = [],
  paginated = false,
  queryScope = "default",
  dateMode = "meeting",
  emailSearch = "",
  postWarmupWithdrawalOnly = false,
}: UseSalesMeetingsQueryParams) => {
  const [{ salesMeetingsPage: currentPage }, setSalesMeetingsQueryState] = useQueryStates(
    {
      salesMeetingsPage: parseAsInteger.withDefault(1),
    },
    { history: "push", shallow: false, clearOnDefault: true },
  );
  const pageSize = SALES_MEETINGS_PAGE_SIZE;
  const resetKey = `${range[0].toISOString()}|${range[1].toISOString()}|${sellerId ?? ""}|${saleStatusFilters.join(",")}|${dateMode}|${emailSearch}|${postWarmupWithdrawalOnly}`;
  const previousResetKey = useRef<string | null>(null);
  const setCurrentPage = (page: number) => {
    void setSalesMeetingsQueryState({ salesMeetingsPage: page });
  };

  useEffect(() => {
    if (!paginated) return;

    if (previousResetKey.current === null) {
      previousResetKey.current = resetKey;
      return;
    }

    if (previousResetKey.current !== resetKey) {
      previousResetKey.current = resetKey;
      void setSalesMeetingsQueryState({ salesMeetingsPage: 1 });
    }
  }, [paginated, resetKey, setSalesMeetingsQueryState]);

  const query = useQuery({
    queryKey: [
      "salesCrmMeetings",
      queryScope,
      range[0].toISOString(),
      range[1].toISOString(),
      sellerId,
      saleStatusFilters,
      dateMode,
      emailSearch,
      postWarmupWithdrawalOnly,
      paginated ? currentPage : null,
      paginated ? pageSize : null,
    ],
    enabled,
    keepPreviousData: true,
    retry: false,
    queryFn: async () => {
      const saleStatusValues = getSaleStatusValues(saleStatusFilters);
      const shouldIncludeEmptySaleStatus = saleStatusFilters.includes(SALE_STATUS_EMPTY_FILTER);

      const searchPattern = escapePostgrestPattern(emailSearch);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const applyFilters = (queryBuilder: any) => {
        // "sale" mode filters by the confirmed bundle purchase date instead of
        // the meeting date (requires the !inner embed on customerBundle).
        let filtered =
          dateMode === "sale"
            ? queryBuilder
                .gte("customerBundle.createdAt", range[0].startOf("day").toISOString())
                .lte("customerBundle.createdAt", range[1].endOf("day").toISOString())
            : queryBuilder
                .gte("startTime", range[0].startOf("day").toISOString())
                .lte("startTime", range[1].endOf("day").toISOString());

        if (sellerId) {
          filtered = filtered.eq("teamMemberId", sellerId);
        }

        if (searchPattern) {
          filtered = filtered.ilike("clientEmail", `%${searchPattern}%`);
        }

        if (postWarmupWithdrawalOnly) {
          filtered = filtered.not("postWarmupWithdrawalAt", "is", null);
        }

        if (shouldIncludeEmptySaleStatus && saleStatusValues.length === 0) {
          filtered = filtered.is("saleStatus", null);
        } else if (!shouldIncludeEmptySaleStatus && saleStatusValues.length > 0) {
          filtered = filtered.in("saleStatus", saleStatusValues);
        } else if (shouldIncludeEmptySaleStatus && saleStatusValues.length > 0) {
          const saleStatusInFilter = saleStatusValues.map((value) => `"${value.replace(/"/g, '\\"')}"`).join(",");
          filtered = filtered.or(`saleStatus.is.null,saleStatus.in.(${saleStatusInFilter})`);
        }

        return filtered;
      };

      const bundleEmbed =
        dateMode === "sale"
          ? `customerBundle:customerBundleId!inner(${salesMeetingBundleSelectFields})`
          : `customerBundle:customerBundleId(${salesMeetingBundleSelectFields})`;

      const fetchPage = async (page: number) => {
        let queryBuilder = applyFilters(
          supabase
            .from("sales_meeting")
            .select(
              `*, teamMember:teamMemberId(id, firstName, lastName, email, calendlyEmail), ${bundleEmbed}, matchedCustomerBundle:matchedCustomerBundleId(${salesMeetingBundleSelectFields})`,
              paginated ? { count: "exact" } : undefined,
            ),
        ).order("startTime", { ascending: false });

        if (paginated) {
          const start = (page - 1) * pageSize;
          const stop = page * pageSize - 1;
          queryBuilder = queryBuilder.range(start, stop).limit(pageSize);
        }

        return queryBuilder;
      };

      let { data, error, count } = await fetchPage(currentPage);

      // The page can come from a stale URL while filters live in component
      // state — a too-large offset makes PostgREST fail with PGRST103.
      // Clamp to the last available page instead of surfacing the error.
      if (error?.code === "PGRST103" && paginated && currentPage > 1) {
        const { count: totalCount, error: countError } = await applyFilters(
          supabase
            .from("sales_meeting")
            .select(dateMode === "sale" ? `id, customerBundle:customerBundleId!inner(createdAt)` : "id", {
              count: "exact",
              head: true,
            }),
        );

        if (!countError) {
          const lastPage = Math.max(1, Math.ceil((totalCount ?? 0) / pageSize));
          ({ data, error, count } = await fetchPage(lastPage));

          if (!error) {
            setCurrentPage(lastPage);
          }
        }
      }

      if (error) throw new Error(error.message);

      return {
        list: (data ?? []) as unknown as SalesMeetingRow[],
        count,
      };
    },
  });

  return {
    ...query,
    data: query.data,
    currentPage,
    setCurrentPage,
    pageSize,
    showSizeChanger: false,
    pageSizeOptions: [String(SALES_MEETINGS_PAGE_SIZE)],
  };
};
